import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { isEqual, isNil } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useInterval } from 'usehooks-ts';
import useUser from '../../hooks/useUser';
import {
  getRecentConversationsRequest,
  setConversations,
} from '../../redux/ducks/ChatDucks';
import type { ChatConversationDataType } from '../../redux/ducks/ChatDucks.types';
import { setCurrentlySelectedLanguage } from '../../redux/ducks/StorageDucks';
import { setFirstDashboardAccess, setOnPwa } from '../../redux/ducks/UIDucks';
import { userGetProfile } from '../../redux/ducks/UserDucks';
import type { RootReducerType } from '../../redux/reducers/RootReducer.types';

type Props = {
  children: React.ReactNode;
};

const NEW_MESSAGES_LONG_POLLING_DELAY = 60000;

const UserProvider = ({ children }: Props) => {
  const { source } = useParams();
  const dispatch = useDispatch();
  const { i18n } = useTranslation();

  const [currentlyLoggedIn, setCurrentlyLoggedIn] = useState<boolean>(false);
  const [onRetrieveNotification, setOnRetrieveNotifications] =
    useState<boolean>(false);
  const { currentUser, isLoggedIn } = useUser();

  const supportedLanguages = useSelector(
    (state: RootReducerType) => state.config.appConfig?.languages,
  );
  const currentlySelectedLanguage = useSelector(
    (state: RootReducerType) => state.storage.currentlySelectedLanguage,
  );
  const { getRecentConversationsResponse, conversations } = useSelector(
    (state: RootReducerType) => state.chat,
  );
  const { updateProfileResponse } = useSelector(
    (state: RootReducerType) => state.user,
  );

  const selectedLanguage = useMemo(
    () => currentlySelectedLanguage,
    [currentlySelectedLanguage],
  );

  const retrieveUserNotifications = useCallback(() => {
    dispatch(getRecentConversationsRequest({}));
    dispatch(setFirstDashboardAccess(false));
  }, [dispatch]);

  useEffect(() => {
    if (!isNil(selectedLanguage)) {
      const shouldUpdate =
        isNil(i18n?.language) || i18n.language !== selectedLanguage;
      if (shouldUpdate) {
        i18n.changeLanguage(selectedLanguage);
      }
    } else {
      // Avoid language overwrite
      /*
      let browserLanguage = navigator.language;
      if (!isEmpty(browserLanguage)) {
        if (browserLanguage.indexOf('-') > 0) {
          browserLanguage = browserLanguage.substring(
            0,
            browserLanguage.indexOf('-'),
          );
        }
      }

      const validLanguages = supportedLanguages?.filter(
        (l) => l.toLowerCase() === browserLanguage.toLowerCase(),
      );

      if (validLanguages && validLanguages.length > 0) {
        dispatch(setCurrentlySelectedLanguage(validLanguages[0]));
      }
        */
    }
  }, [selectedLanguage, i18n]);

  useEffect(() => {
    if (!currentlyLoggedIn) {
      if (isLoggedIn === true) {
        setCurrentlyLoggedIn(true);
      }
    } else if (isLoggedIn !== true && typeof window !== 'undefined') {
      window.location.reload();
    }

    if (isLoggedIn === true && isNil(currentUser)) {
      dispatch(userGetProfile(true));
      setOnRetrieveNotifications(true);
    }

    if (currentUser) {
      if (currentUser.language) {
        if (!selectedLanguage || selectedLanguage !== currentUser.language) {
          dispatch(setCurrentlySelectedLanguage(currentUser.language));
        }
      }

      if (currentUser.team_role) {
        localStorage.setItem('umami.disabled', '1');
      } else {
        localStorage.removeItem('umami.disabled');
      }

      if (onRetrieveNotification) {
        retrieveUserNotifications();
        setOnRetrieveNotifications(false);
      }
    }
  }, [
    isLoggedIn,
    currentUser,
    currentlyLoggedIn,
    dispatch,
    onRetrieveNotification,
    retrieveUserNotifications,
    selectedLanguage,
  ]);

  // update recent conversations
  useEffect(() => {
    if (getRecentConversationsResponse?.data) {
      const newConversations: ChatConversationDataType[] =
        getRecentConversationsResponse.data.conversations.map((c) => ({
          data: c,
        }));

      const allConversations: ChatConversationDataType[] = conversations
        ? [...newConversations, ...conversations.conversations]
        : [...newConversations];

      const sortedConversations: ChatConversationDataType[] = allConversations
        .reduce(
          (
            unique: ChatConversationDataType[],
            item: ChatConversationDataType,
          ) => {
            const existingUnique: ChatConversationDataType[] = unique.filter(
              (x) => x.data.id !== item.data.id,
            );

            return existingUnique.length !== unique.length
              ? unique
              : [...unique, item];
          },
          [],
        )
        .sort(
          (a, b) =>
            (b.data?.last_message?.timestamp || 0) -
            (a.data?.last_message?.timestamp || 0),
        );

      const newConvs = {
        total: getRecentConversationsResponse.data.total_conversations,
        conversations: sortedConversations,
      };

      if (!isEqual(newConvs, conversations)) {
        dispatch(
          setConversations({
            total: getRecentConversationsResponse.data.total_conversations,
            conversations: sortedConversations,
          }),
        );
      }
    }
  }, [getRecentConversationsResponse, conversations, dispatch]);

  useInterval(() => {
    if (currentUser) {
      retrieveUserNotifications();
    }
  }, NEW_MESSAGES_LONG_POLLING_DELAY);

  useEffect(() => {
    if (source && source === 'pwa') {
      dispatch(setOnPwa(true));
    }
  }, [source, dispatch]);

  useEffect(() => {
    if (updateProfileResponse) {
      dispatch(userGetProfile(true));
    }
  }, [updateProfileResponse, dispatch]);

  return children;
};

export default UserProvider;
